import { useEffect, useState, useMemo, useCallback } from "react";
import api, { formatApiError, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  AddressBook, MagnifyingGlass, DownloadSimple, UploadSimple, CheckCircle,
  Trash, ArrowClockwise, Info, GoogleLogo, FileArrowDown,
} from "@phosphor-icons/react";
import { useGeneralSettings } from "@/hooks/useGeneralSettings";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function CompanyContacts() {
  const [tab, setTab] = useState("list");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState("");
  const [counts, setCounts] = useState({});
  const [skip, setSkip] = useState(0);
  const limit = 30;
  const { maskPhone, displayContactName } = useGeneralSettings();
  const totalAll = Object.values(counts).reduce((a, n) => a + (n || 0), 0);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit, skip });
      if (q) params.set("q", q);
      else if (letter) params.set("letter", letter);
      const { data } = await api.get(`/contacts?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [q, letter, skip]);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts/count-by-letter");
      setCounts(data || {});
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { setSkip(0); }, [letter, q]);

  const remove = async (c) => {
    if (!window.confirm(`Excluir contato "${c.name}"?`)) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      toast.success("Contato removido");
      load(); loadCounts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="contacts-page">
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Base</p>
        <h1 className="text-4xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <AddressBook size={32} weight="duotone" /> Contatos
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2">
          Gerencie sua base de contatos. Importe do Google Contacts ou de qualquer planilha.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {[
          { id: "list", label: `Lista (${totalAll})` },
          { id: "import", label: "Importar" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider ${
              tab === t.id ? "bg-[#0F4C3A] text-white" : "bg-[#F7F7F5] text-[#5C5C59] hover:bg-[#E5E5E0]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "list" && (
        <div className="bg-white border border-[#E5E5E0] shadow-brutal p-5" data-testid="contacts-list">
          {/* Search */}
          <div className="flex items-center gap-2 mb-4">
            <MagnifyingGlass size={18} className="text-[#5C5C59]" />
            <Input
              data-testid="contacts-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Pesquisar por nome ou telefone…"
              className="rounded-none flex-1"
            />
            <Button onClick={() => { load(); loadCounts(); }} variant="outline" className="rounded-none" data-testid="refresh-contacts">
              <ArrowClockwise size={14} weight="bold" />
            </Button>
          </div>

          {/* A-Z filter */}
          <div className="flex flex-wrap gap-1 mb-4">
            <button
              onClick={() => { setQ(""); setLetter(""); }}
              data-testid="letter-all"
              className={`px-3 h-8 text-xs font-bold ${
                !letter && !q ? "bg-[#0F4C3A] text-white" : "bg-[#EDF3F0] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
              }`}
              title="Mostrar todos os contatos"
            >
              TODOS
            </button>
            {LETTERS.map((L) => {
              const active = letter === L && !q;
              const has = (counts[L] || 0) > 0;
              return (
                <button
                  key={L}
                  onClick={() => { setQ(""); setLetter(L); }}
                  disabled={!has && !active}
                  data-testid={`letter-${L}`}
                  className={`w-8 h-8 text-xs font-bold ${
                    active ? "bg-[#0F4C3A] text-white" : has ? "bg-[#EDF3F0] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white" : "bg-[#F7F7F5] text-[#C7C7C0]"
                  }`}
                  title={has ? `${counts[L]} contato(s)` : "sem contatos"}
                >
                  {L}
                </button>
              );
            })}
          </div>

          {/* List */}
          {items.length === 0 ? (
            <div className="text-center py-10 text-[#5C5C59] text-sm">
              Nenhum contato encontrado.
              <div className="mt-3">
                <button onClick={() => setTab("import")} className="text-[#0F4C3A] underline text-xs font-semibold">
                  Importar contatos →
                </button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-[#F0F0EC]">
              {items.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2.5" data-testid={`contact-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{displayContactName(c.name, c.phone)}</p>
                    <p className="text-xs font-mono text-[#5C5C59]">{maskPhone(c.phone)}</p>
                    {Object.keys(c.extras || {}).length > 0 && (
                      <p className="text-[10px] text-[#5C5C59] mt-0.5 truncate">
                        {Object.entries(c.extras).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => remove(c)}
                    data-testid={`delete-contact-${c.id}`}
                    className="p-2 border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition-colors"
                    title="Excluir"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-4 text-xs text-[#5C5C59]">
              <span>{skip + 1}-{Math.min(skip + limit, total)} de {total}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-none" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Anterior</Button>
                <Button size="sm" variant="outline" className="rounded-none" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Próximo</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "import" && <ImportPanel onImported={() => { load(); loadCounts(); }} />}
    </div>
  );
}

function ImportPanel({ onImported }) {
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/contacts/template.xlsx`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Falha ao baixar");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "netszap-contatos-template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const doImport = async () => {
    if (!file) return toast.error("Escolha um arquivo");
    setImporting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("overwrite", overwrite ? "true" : "false");
      const { data } = await api.post("/contacts/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.imported} novos, ${data.updated} atualizados, ${data.skipped} ignorados`);
      setFile(null);
      onImported?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="import-panel">
      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
        <h2 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Info size={16} weight="duotone" /> Instruções
        </h2>
        <ol className="space-y-2 text-sm text-[#5C5C59] list-decimal ml-5">
          <li>Baixe a <b>planilha modelo</b> e preencha adequadamente, OU exporte do <b>Google Contatos</b> (contatos.google.com → Exportar → Google CSV).</li>
          <li>Telefone no formato <span className="font-mono">+5511988884444</span> (código do país + DDD + número). BRs sem +55 são convertidos automaticamente.</li>
          <li>Colunas adicionais (email, CPF, data de nascimento) são preservadas em &quot;informações extras&quot; do contato.</li>
          <li>Limite: <b>2.000 contatos</b> por importação. Arquivo até 5MB.</li>
          <li>Linhas com telefone inválido são <b>ignoradas</b> automaticamente.</li>
        </ol>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={downloadTemplate}
            className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white"
            data-testid="download-template-btn"
          >
            <FileArrowDown size={16} weight="bold" className="mr-2" /> Baixar Planilha Modelo
          </Button>
          <a
            href="https://contacts.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#1877F2] text-[#1877F2] text-sm font-semibold hover:bg-[#1877F2] hover:text-white transition-colors"
            data-testid="google-contacts-link"
          >
            <GoogleLogo size={16} weight="bold" /> Exportar do Google Contatos
          </a>
        </div>
      </section>

      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
        <h2 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <UploadSimple size={16} weight="duotone" /> Importar planilha
        </h2>
        <label className="block cursor-pointer">
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            data-testid="file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
          <div className="border-2 border-dashed border-[#0F4C3A] p-8 text-center hover:bg-[#EDF3F0] transition-colors">
            {file ? (
              <>
                <CheckCircle size={32} weight="fill" className="text-[#0F4C3A] mx-auto" />
                <p className="font-semibold mt-2">{file.name}</p>
                <p className="text-xs text-[#5C5C59]">{(file.size / 1024).toFixed(1)} KB · clique para trocar</p>
              </>
            ) : (
              <>
                <DownloadSimple size={32} weight="duotone" className="text-[#0F4C3A] mx-auto" />
                <p className="text-sm mt-2 font-semibold">Clique para selecionar (CSV ou XLSX)</p>
                <p className="text-xs text-[#5C5C59]">ou arraste um arquivo aqui</p>
              </>
            )}
          </div>
        </label>
      </section>

      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
        <h2 className="text-sm font-black uppercase tracking-wider mb-3">Opções</h2>
        <label className="flex items-center justify-between p-3 bg-[#F7F7F5] cursor-pointer">
          <span>
            <span className="font-semibold text-sm">Sobrescrever contatos</span>
            <p className="text-xs text-[#5C5C59]">Se o telefone já existir, atualiza o nome e informações extras.</p>
          </span>
          <Switch checked={overwrite} onCheckedChange={setOverwrite} data-testid="overwrite-switch" />
        </label>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={doImport}
          disabled={!file || importing}
          data-testid="import-submit-btn"
          className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white px-8 py-6 disabled:opacity-40"
        >
          {importing ? "Importando…" : "Importar contatos"}
        </Button>
      </div>

      {result && (
        <section className="bg-[#EDF3F0] border-l-[3px] border-[#0F4C3A] p-4" data-testid="import-result">
          <p className="font-bold flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-[#0F4C3A]" /> Importação concluída</p>
          <div className="grid grid-cols-4 gap-3 mt-3">
            <Stat label="Novos" value={result.imported} color="#0F4C3A" />
            <Stat label="Atualizados" value={result.updated} color="#2563EB" />
            <Stat label="Ignorados" value={result.skipped} color="#D97706" />
            <Stat label="Total" value={result.total_rows} color="#5C5C59" />
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="p-2 bg-white">
      <p className="text-[10px] uppercase tracking-wider text-[#5C5C59]">{label}</p>
      <p className="text-2xl font-black font-mono" style={{ color }}>{value}</p>
    </div>
  );
}
